import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_REASON, CustomReasons } from "components/CustomConfirm/components";
import "components/CustomConfirm/CustomConfirm.scss";

import type { CancellationReason } from "models";

export enum CUSTOM_CONFIRM_TYPES {
  Danger = 1,
  Warning
}

type CustomConfirmProps = {
  type: CUSTOM_CONFIRM_TYPES;
  isOpen: boolean;
  note?: string;
  error?: string;
  confirmMsg: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  customTitle?: string;
  confirmNote?: string;
  customClass?: string;
  placeholderText?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  hideCancelButton?: boolean;
  allowCustomReason?: boolean;
  confirmMandatoryNote?: string;
  declineMandatoryNote?: string;
  confirmReasons?: CancellationReason[];
  handleCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  handleReasonChange?: (value?: number) => void;
  onChangeNote?: (evt: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleConfirm?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  handleDecline?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  handleChangeCustomReason?: (evt: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

export const CustomConfirm = ({
  type,
  note,
  isOpen,
  error = "",
  isLoading,
  isDisabled,
  confirmMsg,
  customTitle,
  confirmNote,
  customClass,
  confirmReasons,
  placeholderText,
  cancelButtonText,
  confirmButtonText,
  hideCancelButton,
  allowCustomReason = false,
  confirmMandatoryNote,
  declineMandatoryNote,
  handleCancel,
  onChangeNote,
  handleDecline,
  handleConfirm,
  handleReasonChange,
  handleChangeCustomReason
}: CustomConfirmProps) => {
  const { t } = useTranslation();

  const [showCustomReason, setShowCustomReason] = useState(false);

  const handleChange = (value?: number) => {
    setShowCustomReason(value === CUSTOM_REASON && allowCustomReason);
    handleReasonChange?.(value);
  };

  const noteLabel = confirmNote || confirmMandatoryNote || declineMandatoryNote;
  const showForm = !!confirmReasons || showCustomReason || !!noteLabel;

  const typeClassName = type === CUSTOM_CONFIRM_TYPES.Danger ? "danger" : "warning";
  const title = type === CUSTOM_CONFIRM_TYPES.Danger ? t("v8_confirmation_required", "Confirmation required") : t("v8_warning", "Warning");

  return (
    <Modal size="tiny" open={isOpen} className={`CustomConfirm ${customClass}`} onClose={handleCancel}>
      <Modal.Header className={typeClassName}>{customTitle || title}</Modal.Header>
      <Modal.Content className={typeClassName}>
        <div className="CustomConfirm-section">
          {type === CUSTOM_CONFIRM_TYPES.Danger && <Icon className="exclamation triangle red" size="big" />}
          <div className="CustomConfirm-inner-section">
            <div className="CustomConfirm-inner-section-heading">{confirmMsg}</div>

            {showForm && (
              <Form>
                {confirmReasons && (
                  <CustomReasons placeholderText={placeholderText} confirmReasons={confirmReasons} handleOnChange={handleChange} allowCustomReason={allowCustomReason} />
                )}
                {showCustomReason && (
                  <Form.Field className="mt-15">
                    <Form.TextArea name="customReason" placeholder={t("v8_enter_a_reason", "Enter a reason")} onChange={handleChangeCustomReason} />
                  </Form.Field>
                )}
                {noteLabel && (
                  <Form.Field>
                    <label>{noteLabel}</label>
                    <Form.TextArea value={note} name="note" onChange={onChangeNote} />
                  </Form.Field>
                )}
              </Form>
            )}

            {!!error && <div className="error">{error}</div>}
          </div>
        </div>
      </Modal.Content>

      <Modal.Actions>
        {!hideCancelButton && (
          <Button onClick={handleCancel} disabled={isLoading} basic>
            {cancelButtonText || t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>
        )}

        {handleDecline ? (
          <Button onClick={handleDecline} loading={isLoading} className={typeClassName}>
            {t("v8_decline", "Decline")}
            <Icon className="xmark" />
          </Button>
        ) : (
          handleConfirm && (
            <Button onClick={handleConfirm} disabled={isDisabled || isLoading} loading={isLoading} className={typeClassName}>
              {confirmButtonText || t("v8_confirm", "Confirm")}
              <Icon className="check" />
            </Button>
          )
        )}
      </Modal.Actions>
    </Modal>
  );
};
